/**
 * Create Task Modal Component
 *
 * Lightweight task creation form (should take ≤15 seconds).
 * System suggests defaults, supervisors may override.
 *
 * Fields:
 * - Linked Alert (read-only, auto-populated)
 * - Task Title (required)
 * - Instructions (optional)
 * - Responsible Area (required, single dropdown - replaces Category + Department)
 * - Severity (system-suggested, can override)
 * - Due Date (optional)
 * - Assign To (optional)
 */

import React, { useState, useEffect } from 'react';
import {
  taskAPI,
  userAPI,
  alertAPI,
  TaskCategory,
  TaskSeverity,
  TaskCreate,
  AlertDetail,
} from '../services/api';
import { usePermissions } from '../hooks/usePermissions';

interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: TaskCategory[];
  onTaskCreated: () => void;
  preselectedAlertId?: number;
}

const SEVERITY_OPTIONS: { value: TaskSeverity; label: string }[] = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

/**
 * Map alert severity to task severity suggestion
 */
const getSuggestedSeverity = (alert: AlertDetail | null): TaskSeverity => {
  if (!alert) return 'medium';
  switch (alert.severity) {
    case 'critical': return 'high';
    case 'warning': return 'medium';
    case 'info': return 'low';
    default: return 'medium';
  }
};

/**
 * Map alert type to suggested category
 */
const getSuggestedCategory = (alert: AlertDetail | null, categories: TaskCategory[]): number | null => {
  if (!alert || categories.length === 0) return categories[0]?.id || null;

  // Map alert types to likely categories
  const categoryMap: Record<string, string[]> = {
    'high_scrap': ['Quality', 'Production'],
    'high_downtime': ['Maintenance', 'Production'],
    'low_efficiency': ['Production', 'Maintenance'],
    'no_data': ['Production', 'IT'],
  };

  const preferredCategories = categoryMap[alert.alert_type] || [];
  for (const preferred of preferredCategories) {
    const found = categories.find(c => c.name.toLowerCase().includes(preferred.toLowerCase()));
    if (found) return found.id;
  }

  return categories[0]?.id || null;
};

/**
 * Get next business day (skip weekends)
 */
const getNextBusinessDay = (): string => {
  const date = new Date();
  date.setDate(date.getDate() + 1);

  // Skip Saturday (6) and Sunday (0)
  while (date.getDay() === 0 || date.getDay() === 6) {
    date.setDate(date.getDate() + 1);
  }

  return date.toISOString().split('T')[0];
};

const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  isOpen,
  onClose,
  categories,
  onTaskCreated,
  preselectedAlertId,
}) => {
  const { user } = usePermissions();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [categoryId, setCategoryId] = useState<number>(0);
  const [severity, setSeverity] = useState<TaskSeverity>('medium');
  const [suggestedSeverity, setSuggestedSeverity] = useState<TaskSeverity>('medium');
  const [dueDate, setDueDate] = useState('');
  const [assignedToUserId, setAssignedToUserId] = useState<number | null>(null);

  // Alert info for display
  const [linkedAlert, setLinkedAlert] = useState<AlertDetail | null>(null);

  // Reference data
  const [users, setUsers] = useState<{ id: number; full_name: string; is_active: boolean }[]>([]);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      resetForm();

      // Fetch alert details if linked
      if (preselectedAlertId) {
        fetchAlertDetails(preselectedAlertId);
      }
    }
  }, [isOpen, preselectedAlertId]);

  const fetchAlertDetails = async (alertId: number) => {
    try {
      const alert = await alertAPI.get(alertId);
      setLinkedAlert(alert);

      // Set suggested values based on alert
      const suggested = getSuggestedSeverity(alert);
      setSuggestedSeverity(suggested);
      setSeverity(suggested);

      const suggestedCategory = getSuggestedCategory(alert, categories);
      if (suggestedCategory) {
        setCategoryId(suggestedCategory);
      }
    } catch (err) {
      console.error('Failed to fetch alert details:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await userAPI.list();
      setUsers(data.filter(u => u.is_active));
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const resetForm = () => {
    setTitle('');
    setInstructions('');
    setSeverity('medium');
    setSuggestedSeverity('medium');
    setDueDate('');
    // Default to current user
    setAssignedToUserId(user?.id || null);
    setLinkedAlert(null);
    setError(null);

    // Set default category if available
    if (categories.length > 0) {
      setCategoryId(categories[0].id);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    if (!categoryId) {
      setError('Please select a responsible area');
      return;
    }

    try {
      setSaving(true);

      const taskData: TaskCreate = {
        title: title.trim(),
        instructions: instructions.trim() || undefined,
        category_id: categoryId,
        severity,
        due_date: dueDate || undefined,
        assigned_to_user_id: assignedToUserId || undefined,
        alert_id: preselectedAlertId || undefined,
      };

      await taskAPI.create(taskData);
      onTaskCreated();
      onClose();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map((e: any) => e.msg || String(e)).join('; '));
      } else if (typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to create task');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div
        className="rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        style={{ background: 'var(--bg-secondary)' }}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
          <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Create Task
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            disabled={saving}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">
              {error}
            </div>
          )}

          {/* Linked Alert (read-only) */}
          {linkedAlert && (
            <div className="px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">LINKED ALERT</span>
              </div>
              <div className="mt-1 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                {linkedAlert.kpi_name}
                {linkedAlert.kpi_value !== null && `: ${linkedAlert.kpi_value}`}
              </div>
              <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                {linkedAlert.machine_name || linkedAlert.department_name}
                {linkedAlert.shift_name && ` • ${linkedAlert.shift_name}`}
              </div>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
              Task Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Investigate downtime spike on Line 3"
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
              autoFocus
            />
          </div>

          {/* Instructions (optional) */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
              Instructions <span className="text-xs font-normal" style={{ color: 'var(--text-secondary)' }}>(optional)</span>
            </label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Any specific steps or notes..."
              rows={2}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
            />
          </div>

          {/* Responsible Area (merged Category) */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
              Responsible Area <span className="text-red-500">*</span>
            </label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(Number(e.target.value))}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
            >
              <option value={0} disabled>Select responsible area</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Severity and Due Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                Severity
              </label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value as TaskSeverity)}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
              >
                {SEVERITY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}{opt.value === suggestedSeverity ? ' (Suggested)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                Due Date <span className="text-xs font-normal" style={{ color: 'var(--text-secondary)' }}>(optional)</span>
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
              />
            </div>
          </div>

          {/* Owner */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
              Owner
            </label>
            <select
              value={assignedToUserId || ''}
              onChange={(e) => setAssignedToUserId(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
            >
              {users.map(user => (
                <option key={user.id} value={user.id}>{user.full_name}</option>
              ))}
            </select>
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium rounded-md disabled:opacity-50"
            style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
          >
            Back
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !title.trim() || !categoryId}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateTaskModal;
